import React, { useState, useMemo, useCallback } from 'react'
import { useShop } from '../hooks'
import { CategoryProduct, SelectedFilter, ShopProductCategoryResponse } from '../models'
import { TOTAL_PRODUCTS_PER_PAGE } from '../consts'
import { AvailableSorting, Filter } from '../models/shop-product-category.model'
import { useTypedSelector } from 'src/State/RootReducer'
import { ShopProductCategoryContext } from '../context/use-shop-product-category-context'

export const INITIAL_PAGINATION_PAGE = 1

const getTotalPagesFromProducts = (productData: ShopProductCategoryResponse | null): number => {
  const totalProducts = productData?.total ?? 0
  return Math.ceil(totalProducts / TOTAL_PRODUCTS_PER_PAGE)
}

interface ShopProviderProps {
  children: React.ReactNode
}

export const ShopProductCategoryProvider: React.FC<ShopProviderProps> = ({ children }) => {
  const [categoryId, setCategoryId] = useState<string>('')
  const [products, setProducts] = useState<CategoryProduct[] | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [totalProducts, setTotalProducts] = useState(0)
  const [filters, setFilters] = useState<Filter[]>([])
  const [activePage, setActivePage] = useState<number>(INITIAL_PAGINATION_PAGE)
  const [totalPages, setTotalPages] = useState<number>(INITIAL_PAGINATION_PAGE)
  const [loadMoreButtonLoading, setLoadMoreButtonLoading] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([])
  const [currentSorting, setCurrentSorting] = useState<string>('')
  const [availableSortings, setAvailableSortings] = useState<AvailableSorting[]>([])
  const shopToken = useTypedSelector(state => state.shop.contextToken)
  const shopSelectedUser = useTypedSelector(state => state.shop.selectedUserData)
  const { getShopCategory, getSearchAllProducts } = useShop()

  const fetchProducts = useCallback(
    async (id: string, page: number, sorting: string, filters?: SelectedFilter[], isSearch?: boolean): Promise<void> => {
      if (products?.length !== 0) {
        setLoadMoreButtonLoading(true)
      }
      try {
        const result = isSearch
          ? await getSearchAllProducts(id, page, sorting, filters)
          : await getShopCategory(id, page, sorting, undefined, filters)

        setProducts((prevProducts: CategoryProduct[] | null) => [...(prevProducts ?? []), ...(result?.elements ?? [])])
        setTotalProducts(result?.total ?? 0)
        setActivePage(result?.page ?? INITIAL_PAGINATION_PAGE)
        setTotalPages(getTotalPagesFromProducts(result))
        setFilters(result?.aggregations ?? [])
        setAvailableSortings(result?.availableSortings ?? [])
        setCurrentSorting(result?.sorting ?? '')
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
        setLoadMoreButtonLoading(false)
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      products,
      shopToken,
      shopSelectedUser,
      setLoadMoreButtonLoading,
      setProducts,
      setTotalProducts,
      setActivePage,
      setTotalPages,
      setFilters,
      setIsLoading,
      setCurrentSorting,
      setAvailableSortings,
    ]
  )

  const contextValue = useMemo(
    () => ({
      categoryId,
      loadMoreButtonLoading,
      products,
      isLoading,
      activePage,
      totalPages,
      totalProducts,
      selectedFilters,
      shopToken,
      shopSelectedUser,
      filters,
      currentSorting,
      availableSortings,
      setCategoryId,
      setSelectedFilters,
      setActivePage,
      setIsLoading,
      setProducts,
      setCurrentSorting,
      setAvailableSortings,
      fetchProducts,
    }),
    [
      categoryId,
      loadMoreButtonLoading,
      shopToken,
      shopSelectedUser,
      products,
      isLoading,
      activePage,
      totalPages,
      totalProducts,
      selectedFilters,
      filters,
      currentSorting,
      availableSortings,
      setCategoryId,
      setSelectedFilters,
      setActivePage,
      setIsLoading,
      setProducts,
      setCurrentSorting,
      setAvailableSortings,
      fetchProducts,
    ]
  )

  return <ShopProductCategoryContext.Provider value={contextValue}>{children}</ShopProductCategoryContext.Provider>
}
